<template>
  <PanelCard v-if="visible" :title="I18n.t('user.panel.file_access_title')">
    <AccessList
      v-if="external"
      testid="user-panel-assigned-files"
      icon="building"
      :label="
        I18n.t('user.panel.assigned_files_count', {
          count: externalRoleItems.length,
        })
      "
      :empty-label="I18n.t('user.panel.no_files_added')"
      :items="externalRoleItems"
      @remove="store.removeRole('externalRoles', $event)"
    />

    <template v-else>
      <div
        class="tw-flex tw-flex-col tw-gap-4"
        data-testid="user-panel-multi-file-access"
      >
        <div>
          <p class="tw-m-0 tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
            {{ I18n.t('user.panel.multi_file_access', { count: filesCount }) }}
          </p>
          <p class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2">
            {{ I18n.t('user.panel.multi_file_access_hint') }}
          </p>
        </div>
        <div
          class="
            tw-flex
            tw-flex-col
            tw-gap-2
            tw-rounded
            tw-bg-sf-slate-subtle-1
            tw-p-4
          "
        >
          <SfCheckbox
            v-model="staffAccess"
            :label="
              I18n.t('user.panel.enable_multi_file_access', {
                count: filesCount,
              })
            "
            :disabled="!staffAccessEditable"
          />
          <p
            v-if="!staffAccessEditable"
            class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2"
          >
            {{ I18n.t('user.accesses.no_staff_access_edit_info') }}
          </p>
        </div>
      </div>

      <hr
        class="
          tw-my-2
          tw-w-full
          tw-border-x-0
          tw-border-b-0
          tw-border-t
          tw-border-solid
          tw-border-sf-slate-lighter-1
        "
      />

      <div
        class="tw-flex tw-flex-col tw-gap-2"
        data-testid="user-panel-specific-file-access"
      >
        <div>
          <p class="tw-m-0 tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
            {{ I18n.t('user.panel.specific_file_access') }}
          </p>
          <p class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2">
            {{ I18n.t('user.panel.specific_file_access_hint') }}
          </p>
        </div>
        <AccessList
          testid="user-panel-firm-roles"
          icon="building"
          :label="
            I18n.t('user.panel.specific_file_access_count', {
              count: firmRoleItems.length,
            })
          "
          :empty-label="I18n.t('user.panel.no_files_added')"
          :items="firmRoleItems"
          :adding="{
            label: I18n.t('user.panel.add_client_file'),
            placeholder: I18n.t('user.panel.search_client_files'),
            search: searchClientFiles,
          }"
          @add="store.addRole('firmRoles', $event)"
          @remove="store.removeRole('firmRoles', $event)"
        />
      </div>

      <template v-if="groupsSupported">
        <hr
          class="
            tw-my-2
            tw-w-full
            tw-border-x-0
            tw-border-b-0
            tw-border-t
            tw-border-solid
            tw-border-sf-slate-lighter-1
          "
        />

        <div
          class="tw-flex tw-flex-col tw-gap-2"
          data-testid="user-panel-group-access"
        >
          <div>
            <p class="tw-m-0 tw-text-sm tw-font-medium tw-text-sf-slate-dark-2">
              {{ I18n.t('user.form.group_access') }}
            </p>
            <p class="tw-m-0 tw-text-sm tw-text-sf-slate-light-2">
              {{ I18n.t('user.panel.group_access_hint') }}
            </p>
          </div>
          <AccessList
            testid="user-panel-groups"
            icon="users"
            :label="
              I18n.t('user.panel.group_access_count', {
                count: groupItems.length,
              })
            "
            :empty-label="I18n.t('user.panel.no_groups_added')"
            :items="groupItems"
            :adding="{
              label: I18n.t('user.panel.add_group'),
              placeholder: I18n.t('user.panel.search_groups'),
              search: searchGroups,
            }"
            @add="store.addGroup"
            @remove="store.removeGroup"
          />
        </div>
      </template>
    </template>
  </PanelCard>
</template>

<script setup lang="ts">
import type {
  AccessListItem,
  AutocompleteOption,
} from '@app/application/user-management/types'

import { computed } from 'vue'

import {
  searchCompanies,
  searchGroups,
} from '@app/application/user-management/api/user-panel'
import { roleKinds, userRoles } from '@app/application/user-management/types'
import {
  roleItems,
  useUserPanelStore,
} from '@app/application/user-management/stores/user-panel'
import useI18n from '@app/shared/vue/composables/use-I18n'
import AccessList from '@app/application/user-management/components/user-panel/access-list.vue'
import PanelCard from '@app/application/user-management/components/panel-card.vue'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox.vue'

defineOptions({ name: 'FileAccessSection' })

const I18n = useI18n()
const store = useUserPanelStore()

const external = computed(() => store.user?.external ?? false)

// An inactive user shows no permission section at all, and a Pulse user holds
// no client file by definition, so it gets no card either.
const visible = computed(
  () =>
    (store.user?.isActive ?? false) && store.externalRole !== userRoles.PULSE
)

const filesCount = computed(() => store.capabilities.firmFilesCount)

const staffAccessEditable = computed(
  () => store.capabilities.staffAccessEditable
)

const groupsSupported = computed(() => store.capabilities.groupsSupported)

const staffAccess = store.draftField('staffAccess')

const externalRoleItems = computed(() =>
  roleItems(store.draft?.externalRoles ?? [])
)

const firmRoleItems = computed(() => roleItems(store.draft?.firmRoles ?? []))

const groupItems = computed<AccessListItem[]>(() =>
  (store.draft?.groups ?? []).map((group) => ({
    key: group.id,
    title: group.name,
    // A group the user just added carries no count, because the autocomplete
    // does not report one.
    subtitle: group.usersCount
      ? I18n.t('user.panel.group_users_count', { count: group.usersCount })
      : undefined,
    removable: group.removable,
  }))
)

const searchClientFiles = (
  query: string,
  signal: AbortSignal
): Promise<AutocompleteOption[] | null> =>
  searchCompanies(store.openUserId, query, roleKinds.FIRM, signal)
</script>
